import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { GridColDef, GridSortModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MinhasCopiasTable from "../components/MinhasCopiasTable";
import { getListaMinhasCopias } from "../../../services/emailLogService";
import { exportarParaExcel } from "../../../utils/exportarExcel";
import ExportExcelButton from "../../../components/ExportExcelButton";
import { getPageName } from "../../../locales/translationUtils";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { ApiResponse } from "../../../services/api";
import { MinhaCopiaResumo } from "../../../types/minhaCopia";
import { useAuth } from "../../../apis/auth/AuthProvider";

/**
 * Módulo "Minhas Cópias" - cópia estrutural de EmailLog/views/ListView.tsx (mesmo
 * layout: NavBar, grid paginada, exportar Excel), mas disponível para qualquer papel
 * (não só ROLE_MASTER) e só com as liberações ("Release") que o usuário logado pode
 * ver - ou todas, se for ROLE_MASTER (ver MinhasCopiasController no back-end).
 *
 * A grid mostra os campos de negócio da liberação (id, consignee, área de remoção, to,
 * modal, liberado por/em), reconstruídos direto da Release - funciona igual para
 * liberações legadas (sem nenhum e-mail rastreado) e para as novas. Cada linha leva ao
 * detalhe (ver ViewById.tsx), que traz todos os campos e, quando há um e-mail
 * vinculado, o status do envio, a cópia do HTML enviado e a opção de reenviar.
 */
const MinhasCopiasListView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { perfilEfetivo } = useAuth();
  const ehMaster = perfilEfetivo === "ROLE_MASTER";

  const [liberacoes, setLiberacoes] = useState<MinhaCopiaResumo[]>([]);
  const [apiResponse, setApiResponse] = useState<ApiResponse<MinhaCopiaResumo> | null>(null);
  const [exportando, setExportando] = useState(false);

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 40,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const buscar = async () => {
    const resposta = await getListaMinhasCopias({
      pageNum: paginationModel.page.toString(),
      quantityPerPage: paginationModel.pageSize.toString(),
      sort: sortModel[0]?.field,
      order: sortModel[0]?.sort ?? undefined,
    });
    setApiResponse(resposta);
    setLiberacoes(resposta?.data?.values ?? []);
  };

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, sortModel]);

  const handleExportarExcel = async () => {
    setExportando(true);

    try {
      const resposta = await getListaMinhasCopias({
        pageNum: '0',
        quantityPerPage: '100000',
      });

      const linhas = (resposta?.data?.values ?? []).map((row) => ({
        [t('carbonCopy.fields.id')]: row.id,
        [t('carbonCopy.fields.consignee')]: row.consignee ?? '',
        [t('carbonCopy.fields.areaDeRemocao')]: row.areaDeRemocao ?? '',
        [t('carbonCopy.fields.to')]: row.to ?? '',
        [t('carbonCopy.fields.modal')]: row.modal ?? '',
        [t('carbonCopy.fields.liberadoPor')]: row.liberadoPor ?? '',
        [t('carbonCopy.fields.liberadoEm')]: row.liberadoEm ? new Date(row.liberadoEm).toLocaleString() : '',
      }));

      await exportarParaExcel(linhas, 'minhas_copias');
    } finally {
      setExportando(false);
    }
  };

  const navItems: NavBarItem[] = [
    { label: "Início", route: "/", iconName: "house-door" },
  ];

  // Sem width/flex de proposito: a grid autodimensiona cada coluna pelo conteudo
  // (autosizeOnMount, ver MinhasCopiasTable.tsx) - so um minWidth de seguranca pra
  // nao nascer largura demais estreita antes do autosize rodar.
  const columns: GridColDef[] = [
    { field: "id", headerName: t('carbonCopy.fields.id'), minWidth: 70 },
    { field: "consignee", headerName: t('carbonCopy.fields.consignee'), minWidth: 160 },
    { field: "areaDeRemocao", headerName: t('carbonCopy.fields.areaDeRemocao'), minWidth: 130 },
    { field: "to", headerName: t('carbonCopy.fields.to'), minWidth: 160 },
    { field: "modal", headerName: t('carbonCopy.fields.modal'), minWidth: 90 },
    { field: "liberadoPor", headerName: t('carbonCopy.fields.liberadoPor'), minWidth: 150 },
    {
      field: "liberadoEm",
      headerName: t('carbonCopy.fields.liberadoEm'),
      minWidth: 150,
      valueFormatter: (value: any) => value ? new Date(value).toLocaleString() : '-',
    },
  ];

  return (
    <Container
      fluid
      style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}
    >
      <NavBar items={navItems} />
      <div className="mb-2"></div>
      <h2>{getPageName("minhasCopias")}</h2>
      <p className="text-muted mb-2" style={{ fontSize: "13px" }}>
        {t(ehMaster ? "carbonCopy.subtitleMaster" : "carbonCopy.subtitle")}
      </p>

      <div className="d-flex justify-content-end mb-2">
        <ExportExcelButton onClick={handleExportarExcel} exportando={exportando} />
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <MinhasCopiasTable
          rows={liberacoes}
          columns={columns}
          apiResponse={apiResponse}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onRowClick={(row) => navigate(`/carbon-copies/${row.id}`)}
        />
      </div>
    </Container>
  );
};

export default MinhasCopiasListView;
